import { ProviderActivitiesTableData } from "@components/providerActivities/providerActivitiesTable";
import { TableContainer } from "@components/table/tableContainer";
import {
    useGetProviderActivitiesQuery,
    useRemoveProviderActivityMutation
} from "@graphql2/types";
import { ProviderActivitiesForReviewTable } from "@components/providerActivities/providerActivitiesForReviewTable";
import { Button, Card, DatePicker } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import moment from "moment";
import * as React from "react";
import { FormattedMessage } from "react-intl";

export const ReviewProviderActivitiesPage: React.FC = () => {
    const [date, setDate] = React.useState<moment.Moment | undefined>(moment());

    const { data, loading, error, refetch } = useGetProviderActivitiesQuery({
        variables: {
            date: moment().utc().startOf("day").valueOf(),
            reviewStatus: "review"
        },
        // Disable cache because returned records can have duplicated IDs.
        fetchPolicy: "no-cache"
    });

    const [removeActivity] = useRemoveProviderActivityMutation({
        refetchQueries: ["getProviderActivities"]
    });

    const handleRemoveActivity = async (id: string, segmentId: string) => {
        await removeActivity({
            variables: { id, segmentId }
        });
    };

    React.useEffect(() => {
        if (!date) {
            return;
        }

        refetch({
            date: date.clone().utc().startOf("day").valueOf(),
            reviewStatus: "review"
        });
    }, [date, refetch]);

    const handlePreviousDate = () => {
        setDate(prev => (prev ? prev.clone().subtract(1, "day") : moment()));
    };

    const handleNextDate = () => {
        setDate(prev => (prev ? prev.clone().add(1, "day") : moment()));
    };

    if (error) {
        return null;
    }

    return (
        <Card
            extra={(
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Button
                        icon={<LeftOutlined />}
                        onClick={handlePreviousDate}
                    />

                    <DatePicker
                        clearIcon={false}
                        value={date}
                        onChange={d => setDate(d || undefined)}
                    />

                    <Button
                        disabled={!date || date.isSame(moment(), "day")}
                        icon={<RightOutlined />}
                        onClick={handleNextDate}
                    />
                </div>
            )}
            title={<FormattedMessage id="providerActivities" />}
        >
            <TableContainer
                dataSource={
                    data?.providerActivities as ProviderActivitiesTableData[] || []
                }
                handlers={{
                    removeActivity: handleRemoveActivity
                }}
                loading={loading}
                pagination={undefined}
                TableComponent={ProviderActivitiesForReviewTable}
            />
        </Card>
    );
};
